import {
  IGetListYearGroupService,
  YearGroup,
} from "../../../../abstractions/services/actions/IGetListYearGroupService";
import { BaseItemResponse } from "../../../../base/response/BaseItemResponse";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import { MonitoreoBrocaResponse } from "../response/MonitoreoBrocaResponse";
import { fx_createMonitoreoBrocaGrpcService } from "./MonitoreoBrocaGrpcService";

/** Grupo de monitoreos de broca de una semana ISO: todos los lotes monitoreados
 *  esa semana (puede haber varios, monitoreados en días distintos). */
export type MonitoreoBrocaSemanaGroup = BaseItemResponse & {
  /** Año de la semana (redundante con el grupo de año contenedor). */
  anio: number;
  /** Semana ISO del año. */
  semana: number;
  /** Monitoreos de esa semana (uno o más lotes). */
  registros: MonitoreoBrocaResponse[];
};

/** Servicio que retorna los monitoreos de broca de una finca agrupados por año
 *  y, dentro de cada año, por semana ISO (años y semanas descendente). No
 *  extiende `BaseGetYearGroupListService` porque ese agrupa 1 item = 1 fila;
 *  acá cada fila de semana agrupa los monitoreos de todos los lotes de esa
 *  semana. Se apoya en el list service plano de la finca, así que hereda su
 *  sync delta e invalidaciones de cache. */
class MonitoreoBrocaGetSemanaGroupListByFincaIdService
  implements IGetListYearGroupService<MonitoreoBrocaSemanaGroup>
{
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<YearGroup<MonitoreoBrocaSemanaGroup>[]> {
    const items = await fx_createMonitoreoBrocaGrpcService(
      this._fincaId,
    ).getListAsync();
    return this._group(items);
  }

  /** Agrupa por año (desc) y, dentro de cada año, por semana ISO (desc); los
   *  registros de cada semana quedan ordenados por fecha descendente. */
  private _group(
    items: MonitoreoBrocaResponse[],
  ): YearGroup<MonitoreoBrocaSemanaGroup>[] {
    const byYear = new Map<number, Map<number, MonitoreoBrocaResponse[]>>();
    for (const item of items) {
      if (!byYear.has(item.anio)) byYear.set(item.anio, new Map());
      const bySemana = byYear.get(item.anio)!;
      if (!bySemana.has(item.semanaDelAnio)) bySemana.set(item.semanaDelAnio, []);
      bySemana.get(item.semanaDelAnio)!.push(item);
    }

    return [...byYear.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([anio, bySemana]) => ({
        year: anio,
        items: [...bySemana.entries()]
          .sort((a, b) => b[0] - a[0])
          .map(([semana, registros]) => ({
            id: `${anio}-${semana}`,
            isEliminado: false,
            anio,
            semana,
            registros: [...registros].sort((a, b) =>
              b.fecha.localeCompare(a.fecha),
            ),
          })),
      }));
  }
}

/** Factory cacheada: retorna una única instancia por fincaId. */
export const fx_createMonitoreoBrocaGetSemanaGroupListByFincaIdService =
  createCachedFactory(
    (fincaId: string) =>
      new MonitoreoBrocaGetSemanaGroupListByFincaIdService(fincaId),
  );
